import BlockIcon from "@mui/icons-material/Block"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetTenantsByUserIdQuery,
  useGetTenantsQuery,
  useSaCreateThingsMutation,
} from "../features/things/thingsSaApi"
import { useNotification } from "./NotificationProvider"

interface AdminTenantsListProps {
  administratorId: string
}

export const AdminTenantsList: FC<AdminTenantsListProps> = ({
  administratorId,
}) => {
  const { t } = useTranslation()

  const [searchAccessible, setSearchAccessible] = useState("")
  const [searchInaccessible, setSearchInaccessible] = useState("")
  const [isCreating, setIsCreating] = useState(false)

  const allTenantsQuery = useGetTenantsQuery({})
  const userTenantsQuery = useGetTenantsByUserIdQuery({
    userId: administratorId,
  })
  const [createThings] = useSaCreateThingsMutation()

  const { showNotification } = useNotification()

  const allTenants = allTenantsQuery.data?.things || []
  const userTenants = userTenantsQuery.data?.things || []

  const isLoading = allTenantsQuery.isLoading || userTenantsQuery.isLoading
  const hasError = allTenantsQuery.error || userTenantsQuery.error

  // Get tenant IDs that the user has access to
  const userTenantIds = useMemo(() => {
    return new Set(userTenants.map((tenant: any) => tenant.id))
  }, [userTenants])

  // Separate tenants into accessible and inaccessible
  const accessibleTenants = useMemo(() => {
    return allTenants.filter((tenant: any) => userTenantIds.has(tenant.id))
  }, [allTenants, userTenantIds])

  const inaccessibleTenants = useMemo(() => {
    return allTenants.filter((tenant: any) => !userTenantIds.has(tenant.id))
  }, [allTenants, userTenantIds])

  // Filter accessible tenants based on search
  const filteredAccessibleTenants = useMemo(() => {
    const search = searchAccessible.trim().toLowerCase()
    if (!search) return accessibleTenants
    return accessibleTenants.filter(
      (tenant: any) =>
        (tenant.name || "").toLowerCase().includes(search) ||
        (tenant.id || "").toLowerCase().includes(search) ||
        (tenant.description || "").toLowerCase().includes(search),
    )
  }, [accessibleTenants, searchAccessible])

  // Filter inaccessible tenants based on search
  const filteredInaccessibleTenants = useMemo(() => {
    const search = searchInaccessible.trim().toLowerCase()
    if (!search) return inaccessibleTenants
    return inaccessibleTenants.filter(
      (tenant: any) =>
        (tenant.name || "").toLowerCase().includes(search) ||
        (tenant.id || "").toLowerCase().includes(search) ||
        (tenant.description || "").toLowerCase().includes(search),
    )
  }, [inaccessibleTenants, searchInaccessible])

  if (hasError) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="error">
            {t("errorLoadingTenants")}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{t("tenantAccess")}</Typography>
          <Typography variant="h6" color="text.secondary">
            ({allTenants.length} total)
          </Typography>
        </Stack>

        {isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2">{t("loading")}</Typography>
          </Stack>
        ) : (
          <Stack direction="row" spacing={2}>
            {/* Accessible Tenants Column */}
            <Box sx={{ flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <CheckCircleIcon color="success" />
                <Typography variant="subtitle1">
                  {t("tenantsWithAccess")} ({filteredAccessibleTenants.length})
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  color="success"
                  disabled={isCreating}
                  onClick={async () => {
                    setIsCreating(true)
                    try {
                      await createThings({
                        params: { tenantId: "noId" },
                        body: {
                          things: [
                            {
                              tenantId: "noId",
                              thingTypeMrn: "mrn:sl2:::actionReq",
                              executionMode: "sync",
                              actionType: "GrantAccessToAllTenants",
                              inputData: { administratorId },
                              idempotencyKey: `grant-${administratorId}-${Date.now()}`,
                            },
                          ],
                        },
                      })
                      showNotification(t("accessGrantSuccess"), "success")
                      // Refetch the queries to update the lists
                      allTenantsQuery.refetch()
                      userTenantsQuery.refetch()
                    } catch (error) {
                      showNotification(t("accessGrantFailed"), "error")
                    } finally {
                      setIsCreating(false)
                    }
                  }}
                >
                  {isCreating ? t("submitting") : t("grantAccessToAllTenants")}
                </Button>
              </Stack>
              <TextField
                size="small"
                label={t("search")}
                placeholder={t("searchTenants")}
                value={searchAccessible}
                onChange={(e) => setSearchAccessible(e.target.value)}
                fullWidth
                sx={{ mb: 1 }}
              />
              <Box
                sx={{
                  maxHeight: 1200,
                  minHeight: 400,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 1,
                }}
              >
                {filteredAccessibleTenants.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 2 }}
                  >
                    {accessibleTenants.length === 0
                      ? t("noTenantsAssigned")
                      : t("noTenantsMatchSearch")}
                  </Typography>
                ) : (
                  <List dense>
                    {filteredAccessibleTenants.map((tenant: any) => (
                      <ListItem key={tenant.id}>
                        <ListItemText
                          primary={tenant.name || tenant.id}
                          secondary={tenant.description || tenant.id}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            {/* Inaccessible Tenants Column */}
            <Box sx={{ flex: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <BlockIcon color="error" />
                <Typography variant="subtitle1">
                  {t("tenantsWithoutAccess")} (
                  {filteredInaccessibleTenants.length})
                </Typography>
              </Stack>
              <Box py={3.3} />
              <TextField
                size="small"
                label={t("search")}
                placeholder={t("searchTenants")}
                value={searchInaccessible}
                onChange={(e) => setSearchInaccessible(e.target.value)}
                fullWidth
                sx={{ mb: 1 }}
              />
              <Box
                sx={{
                  maxHeight: 1200,
                  minHeight: 400,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 1,
                }}
              >
                {filteredInaccessibleTenants.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 2 }}
                  >
                    {inaccessibleTenants.length === 0
                      ? t("allTenantsAssigned")
                      : t("noTenantsMatchSearch")}
                  </Typography>
                ) : (
                  <List dense>
                    {filteredInaccessibleTenants.map((tenant: any) => (
                      <ListItem key={tenant.id}>
                        <ListItemText
                          primary={tenant.name || tenant.id}
                          secondary={tenant.description || tenant.id}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

export default AdminTenantsList
